/**
 * Auto-layout engine for the CanvasKit editor.
 *
 * The implementation now lives in shared-util (visual-rendering/scene-layout)
 * as the single source of truth shared with the API's canvas design agent —
 * editor preview and agent-authored scenes lay out identically. This module is
 * a thin re-export that preserves the editor-facing SceneDocument type (the
 * Orval-generated SceneDocumentDto, structurally identical to shared-util's
 * SceneDocument) across the call.
 */

import {
  computeLayout as sharedComputeLayout,
  applyConstraints as sharedApplyConstraints,
  commitDocSize as sharedCommitDocSize,
  tightenGroups as sharedTightenGroups,
} from '@nexmail/shared-util';
import type { SceneDocument } from '@/lib/scene-document';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Apply auto-layout to all FRAME/GROUP nodes whose layoutMode is not 'NONE'.
 * Returns the same `doc` reference when there are no auto-layout nodes.
 */
export function computeLayout(doc: SceneDocument): SceneDocument {
  return sharedComputeLayout(doc as never) as unknown as SceneDocument;
}

/**
 * Re-derive every plain GROUP's box from the union of its children. Idempotent,
 * and never changes where anything renders.
 */
export function tightenGroups(doc: SceneDocument): SceneDocument {
  return sharedTightenGroups(doc as never) as unknown as SceneDocument;
}

/**
 * Propagate a FRAME/GROUP resize to its children via their constraint settings.
 * Pass oldBounds = bounds BEFORE the resize, newBounds = bounds AFTER.
 */
export function applyConstraints(
  doc: SceneDocument,
  frameId: string,
  oldBounds: Rect,
  newBounds: Rect,
): SceneDocument {
  return sharedApplyConstraints(
    doc as never,
    frameId,
    oldBounds,
    newBounds,
  ) as unknown as SceneDocument;
}

/**
 * Layout bake + doc-size sync: for single-root docs, doc.width/height follow
 * the laid-out root frame (hugging artboards). Multi-root (workspace) docs get
 * the layout bake only.
 */
export function commitDocSize(doc: SceneDocument): SceneDocument {
  return sharedCommitDocSize(doc as never) as unknown as SceneDocument;
}
